/**
 * The till's function buttons: which actions exist, and the rules a stored layout must obey.
 *
 * Code defines what an action does (the till's registry); data defines where it appears (the
 * `till_button` rows an administrator edits). This module is the part both sides share, so the
 * API refuses exactly the layouts the editor would flag.
 *
 * Action ids are stable forever: a stored layout, an exported JSON file and an older till may all
 * still name one. Append, never rename.
 */

export type TillActionCategory = 'bill' | 'line' | 'payment' | 'customer' | 'navigation' | 'system';

export interface TillActionSpec {
  id: string;
  label: string;
  category: TillActionCategory;
  /** The `keyboard_shortcut` action this replaces; its key is kept in that table for older tills. */
  legacy?: string;
  /** Default key for a new button of this action. Legacy actions take theirs from `keyboard_shortcut`. */
  defaultKey?: string;
  /** Validates a button's params; returns a problem or null. Actions without params accept only none. */
  params?: (params: Record<string, unknown>) => string | null;
}

export const TILL_ACTIONS: readonly TillActionSpec[] = [
  { id: 'nav.find', label: 'Find anything', category: 'navigation', legacy: 'productSearch' },
  { id: 'customer.attach', label: 'Attach customer', category: 'customer', legacy: 'customer' },
  { id: 'line.quantity', label: 'Edit quantity', category: 'line', legacy: 'quantity' },
  { id: 'line.removeLast', label: 'Remove last line', category: 'line', legacy: 'removeItem' },
  { id: 'bill.hold', label: 'Hold bill', category: 'bill', legacy: 'holdBill' },
  { id: 'bill.discount', label: 'Bill discount', category: 'bill', legacy: 'discount' },
  { id: 'customer.lookup', label: 'Customer lookup', category: 'customer', legacy: 'customerLookup' },
  { id: 'bill.resume', label: 'Resume held bill', category: 'bill', legacy: 'resumeBill' },
  { id: 'payment.take', label: 'Take payment', category: 'payment', legacy: 'payment' },
  { id: 'bill.reprint', label: 'Reprint last receipt', category: 'bill', legacy: 'reprintReceipt' },
  { id: 'bill.new', label: 'New bill', category: 'bill', legacy: 'newSale' },
  { id: 'bill.noTax', label: 'No Tax', category: 'bill', defaultKey: 'Alt+T' },
  { id: 'customer.remove', label: 'Remove customer', category: 'customer', defaultKey: 'Alt+R' },
  {
    id: 'bill.discountPreset',
    label: 'Bill discount preset',
    category: 'bill',
    params: (params) => {
      const bps = params.percentBps;
      return typeof bps === 'number' && Number.isInteger(bps) && bps > 0 && bps <= 10_000
        ? null
        : 'Give the discount as a whole percentage in basis points (500 = 5%), from 1 to 10000.';
    },
  },
  { id: 'system.customerDisplay', label: 'Customer screen', category: 'system' },
  { id: 'line.openItem', label: 'Open item', category: 'line', defaultKey: 'Alt+O' },
  { id: 'line.itemTax', label: 'Item tax', category: 'line', defaultKey: 'Alt+I' },
];

export const TILL_ACTION_IDS: readonly string[] = TILL_ACTIONS.map((action) => action.id);

export function tillAction(id: string): TillActionSpec | undefined {
  return TILL_ACTIONS.find((action) => action.id === id);
}

export interface TillButton {
  id: string;
  actionId: string;
  params: Record<string, unknown> | null;
  /** Label overrides; the action's own label (translated) is used when absent. */
  label: { en?: string; ta?: string } | null;
  keyCombo: string | null;
  page: number;
  position: number;
  size: '1x1' | '2x1';
  tone: 'neutral' | 'primary' | 'warn' | 'danger' | 'success' | null;
  visible: boolean;
}

export interface TillLayout {
  /** Bumped on every save; a save must name the version it was based on. */
  version: number;
  buttons: TillButton[];
  /** True when nothing is stored and these are the built-in defaults. */
  isDefault: boolean;
}

/**
 * The built-in layout. Only actions that have no other control on the till, so with their features
 * switched off the bar is empty and the till looks exactly as it always has.
 * Open item and Item Tax show only when the store turns open items on (and the API advertises it).
 */
export const DEFAULT_TILL_BUTTONS: readonly TillButton[] = [
  { id: 'default:noTax', actionId: 'bill.noTax', params: null, label: null, keyCombo: 'Alt+T', page: 0, position: 0, size: '1x1', tone: 'warn', visible: true },
  { id: 'default:openItem', actionId: 'line.openItem', params: null, label: null, keyCombo: 'Alt+O', page: 0, position: 1, size: '1x1', tone: null, visible: true },
  { id: 'default:itemTax', actionId: 'line.itemTax', params: null, label: null, keyCombo: 'Alt+I', page: 0, position: 2, size: '1x1', tone: null, visible: true },
];

// ── keys ───────────────────────────────────────────────────────────────────────────────────

const MODIFIERS = ['Ctrl', 'Alt', 'Shift'] as const;
const FUNCTION_KEY = /^F([1-9]|1[0-2])$/;

/**
 * Keys the browser (or Windows) keeps for itself, or that typing needs. A page cannot reliably
 * capture them, and binding one would either do nothing or close the till.
 */
export const RESERVED_KEYS: readonly string[] = [
  'Ctrl+W', 'Ctrl+T', 'Ctrl+N', 'Ctrl+Tab', 'Ctrl+Shift+T', 'Ctrl+Shift+N', 'Ctrl+Shift+W', 'Ctrl+Shift+Tab',
  'Ctrl+L', 'Ctrl+R', 'Ctrl+Shift+R', 'Ctrl+Shift+I', 'Ctrl+Shift+J', 'Ctrl+F4', 'Alt+F4', 'Alt+Tab',
  'Alt+D', 'Alt+E', 'Alt+F', 'Alt+Home', 'Alt+ArrowLeft', 'Alt+ArrowRight', 'F1',
  // Editing keys every text field needs, and the till's own palette.
  'Ctrl+A', 'Ctrl+C', 'Ctrl+V', 'Ctrl+X', 'Ctrl+Z', 'Ctrl+Y', 'Ctrl+K', 'Ctrl+F', 'Ctrl+P', 'Escape',
];

/** "alt + t" -> "Alt+T". Null when it is not a key combination at all. */
export function normalizeKeyCombo(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const parts = raw.split('+').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  const keyPart = parts[parts.length - 1]!;
  const modifiers = new Set<string>();
  for (const part of parts.slice(0, -1)) {
    const modifier = MODIFIERS.find((name) => name.toLowerCase() === part.toLowerCase() || (name === 'Ctrl' && part.toLowerCase() === 'control'));
    if (!modifier) return null;
    modifiers.add(modifier);
  }
  let key = keyPart;
  if (/^f([1-9]|1[0-2])$/i.test(key)) key = key.toUpperCase();
  else if (key.length === 1) key = key.toUpperCase();
  else if (key.toLowerCase() === 'esc' || key.toLowerCase() === 'escape') key = 'Escape';
  else key = key[0]!.toUpperCase() + key.slice(1);
  return [...MODIFIERS.filter((name) => modifiers.has(name)), key].join('+');
}

/**
 * Why a key cannot be used, or null. A plain letter, digit or Enter is what a barcode scanner
 * types, so a shortcut must be a function key or use Ctrl / Alt.
 */
export function keyComboProblem(raw: string): string | null {
  const combo = normalizeKeyCombo(raw);
  if (!combo) return `"${raw}" is not a key. Use a function key (F2) or Ctrl / Alt with a key (Alt+T).`;
  if (RESERVED_KEYS.includes(combo)) return `${combo} belongs to the browser or to typing and cannot be used.`;
  const parts = combo.split('+');
  const key = parts[parts.length - 1]!;
  const hasCtrlOrAlt = parts.includes('Ctrl') || parts.includes('Alt');
  if (FUNCTION_KEY.test(key)) return null;
  if (!hasCtrlOrAlt) return `${combo} is typed by barcode scanners. Use a function key or add Ctrl / Alt.`;
  return null;
}

/** The combo a keyboard event stands for, in the same form as `normalizeKeyCombo`. */
export function comboOfEvent(event: { key: string; ctrlKey: boolean; altKey: boolean; shiftKey: boolean; metaKey?: boolean; code?: string }): string | null {
  if (['Control', 'Alt', 'Shift', 'Meta'].includes(event.key)) return null;
  // With Alt held, Windows layouts may report a different `key`; the physical key is what was meant.
  let key = event.key;
  if ((event.altKey || event.ctrlKey) && event.code && /^Key[A-Z]$/.test(event.code)) key = event.code.slice(3);
  else if ((event.altKey || event.ctrlKey) && event.code && /^Digit[0-9]$/.test(event.code)) key = event.code.slice(5);
  const parts: string[] = [];
  if (event.ctrlKey || event.metaKey) parts.push('Ctrl');
  if (event.altKey) parts.push('Alt');
  if (event.shiftKey) parts.push('Shift');
  parts.push(key);
  return normalizeKeyCombo(parts.join('+'));
}

// ── layout validation ─────────────────────────────────────────────────────────────────────

export interface LayoutProblem {
  index: number | null;
  field: string;
  message: string;
}

const LABEL_MAX = 40;

/**
 * Every rule a saved layout must obey. `legacyKeys` are the `keyboard_shortcut` keys of built-in
 * actions that have no button in this layout, so a new button cannot steal one.
 */
export function validateTillLayout(buttons: TillButton[], legacyKeys: { action: string; keyCode: string }[] = []): LayoutProblem[] {
  const problems: LayoutProblem[] = [];
  if (buttons.length > 120) problems.push({ index: null, field: 'buttons', message: 'A layout holds at most 120 buttons.' });

  const slots = new Set<string>();
  const ids = new Set<string>();
  const keys = new Map<string, string>();
  for (const entry of legacyKeys) {
    const combo = normalizeKeyCombo(entry.keyCode);
    if (combo) keys.set(combo, `the ${entry.action} key`);
  }

  buttons.forEach((button, index) => {
    const spec = tillAction(button.actionId);
    if (!spec) {
      problems.push({ index, field: 'actionId', message: `There is no action called ${button.actionId}.` });
      return;
    }
    if (!button.id || ids.has(button.id)) problems.push({ index, field: 'id', message: 'Every button needs its own id.' });
    ids.add(button.id);

    if (!Number.isInteger(button.page) || button.page < 0 || button.page > 9) problems.push({ index, field: 'page', message: 'Use a page from 0 to 9.' });
    if (!Number.isInteger(button.position) || button.position < 0 || button.position > 199) problems.push({ index, field: 'position', message: 'Use a position from 0 to 199.' });
    const slot = `${button.page}:${button.position}`;
    if (slots.has(slot)) problems.push({ index, field: 'position', message: 'Two buttons are in the same place.' });
    slots.add(slot);
    if (button.size !== '1x1' && button.size !== '2x1') problems.push({ index, field: 'size', message: 'Size is 1x1 or 2x1.' });

    for (const language of ['en', 'ta'] as const) {
      const text = button.label?.[language];
      if (text !== undefined && (typeof text !== 'string' || text.trim().length === 0 || text.length > LABEL_MAX)) {
        problems.push({ index, field: `label.${language}`, message: `A label is 1 to ${LABEL_MAX} characters.` });
      }
    }

    if (spec.params) {
      if (button.params !== null && button.params !== undefined && (typeof button.params !== 'object' || Array.isArray(button.params))) {
        problems.push({ index, field: 'params', message: `${spec.label} settings must be an object.` });
      } else {
        const problem = spec.params(button.params ?? {});
        if (problem) problems.push({ index, field: 'params', message: problem });
      }
    } else if (button.params && Object.keys(button.params).length > 0) {
      problems.push({ index, field: 'params', message: `${spec.label} takes no settings.` });
    }

    if (button.keyCombo) {
      const problem = keyComboProblem(button.keyCombo);
      if (problem) {
        problems.push({ index, field: 'keyCombo', message: problem });
      } else {
        const combo = normalizeKeyCombo(button.keyCombo)!;
        const owner = keys.get(combo);
        if (owner) problems.push({ index, field: 'keyCombo', message: `${combo} is already used by ${owner}.` });
        else keys.set(combo, `"${button.label?.en ?? spec.label}"`);
      }
    }
  });
  return problems;
}
